/**
 * Multi-Select-State des Akte-Browsers: Set der ausgewählten Datei-Ids + Anker für
 * Shift-Bereichsauswahl. Rein lokaler View-State (kein Server). Die Reihenfolge für
 * Bereichsauswahl kommt pro Klick von der aufrufenden Liste (sichtbare Datei-Ids), damit
 * der Hook nichts über Sortierung/Filter wissen muss.
 */
import { useCallback, useState } from 'react';

export interface SelectionMods {
  /** Shift → Bereich ab Anker bis zur geklickten Datei (ersetzt die Auswahl). */
  shift: boolean;
  /** Ctrl/Cmd → einzelnes Toggle, restliche Auswahl bleibt. */
  toggle: boolean;
}

/**
 * Warum die letzte Auswahl entstand.
 *
 * `pick` ist ein schlichter Klick auf die Zeile: „zeig mir diese Datei". `collect` ist
 * Checkbox, Ctrl, Shift oder „alle auswählen": „ich baue eine Menge". Auf Desktop macht das
 * keinen Unterschied — mobil schon: nur ein `pick` darf das Detail-Sheet öffnen, sonst legt sich
 * das Sheet nach der ersten Checkbox über die Liste und die zweite Datei ist unerreichbar.
 */
export type SelectionIntent = 'pick' | 'collect';

export interface AkteSelectionApi {
  selectedIds: ReadonlySet<string>;
  count: number;
  intent: SelectionIntent;
  isSelected: (id: string) => boolean;
  /** Klick auf eine Datei-Zeile/Kachel; interpretiert Shift/Ctrl relativ zur sichtbaren Reihenfolge. */
  handleClick: (id: string, orderedIds: readonly string[], mods: SelectionMods) => void;
  /** Checkbox-Toggle einer einzelnen Datei (setzt zugleich den Anker). */
  toggle: (id: string) => void;
  /** Ersetzt die Auswahl komplett (z. B. „Alle im Ordner"). */
  replaceAll: (ids: readonly string[]) => void;
  clear: () => void;
}

function rangeBetween(orderedIds: readonly string[], a: string, b: string): string[] {
  const ia = orderedIds.indexOf(a);
  const ib = orderedIds.indexOf(b);
  if (ia === -1 || ib === -1) return [b];
  const [lo, hi] = ia <= ib ? [ia, ib] : [ib, ia];
  return orderedIds.slice(lo, hi + 1);
}

function withToggled(prev: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(prev);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
}

export function useAkteSelection(): AkteSelectionApi {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [intent, setIntent] = useState<SelectionIntent>('pick');

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const handleClick = useCallback(
    (id: string, orderedIds: readonly string[], mods: SelectionMods) => {
      if (mods.shift && anchorId) {
        setSelectedIds(new Set(rangeBetween(orderedIds, anchorId, id)));
        setIntent('collect');
        return;
      }
      if (mods.toggle) {
        setSelectedIds((prev) => withToggled(prev, id));
        setAnchorId(id);
        setIntent('collect');
        return;
      }
      setSelectedIds(new Set([id]));
      setAnchorId(id);
      setIntent('pick');
    },
    [anchorId],
  );

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => withToggled(prev, id));
    setAnchorId(id);
    setIntent('collect');
  }, []);

  const replaceAll = useCallback((ids: readonly string[]) => {
    setSelectedIds(new Set(ids));
    setAnchorId(ids.length > 0 ? ids[ids.length - 1]! : null);
    setIntent('collect');
  }, []);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
    setAnchorId(null);
    setIntent('pick');
  }, []);

  return {
    selectedIds,
    count: selectedIds.size,
    intent,
    isSelected,
    handleClick,
    toggle,
    replaceAll,
    clear,
  };
}
